import {
  Badge,
  Box,
  Button,
  Center,
  createStyles,
  Flex,
  Grid,
  Loader,
  Modal,
  Skeleton,
  Text,
} from "@mantine/core";
import React, { useEffect, useState, useMemo } from "react";
import NoteModalNew from "../../../components/NoteModal/NoteModalNew";
import { useSelector } from "react-redux";
import moment from "moment";
import { getSingleNote } from "../../../services/note";
import DocumentNotificationCard from "./DocumentActions";
import { useDisclosure } from "@mantine/hooks";
import PendingActionsDrawer from "./PendingActionsDrawer";
import { getMyComplianceTasks, updateOperationalTaskStatus } from "../../../services/compliance";
import { isStaff } from "../../../http";
import schedulerService from "../../../services/scheduler";
import ShiftDrawer from "../Scheduler/ShiftDrawer";
import { isoDate, fmtRange, entryMeta } from "../Scheduler/scheduleUtils";
import { useNavigate } from "react-router-dom";
import {
  IconCheck,
  IconCircleDashed,
  IconPlayerPlay,
  IconCalendarDue,
  IconChevronRight,
  IconChevronLeft,
  IconFilter,
  IconCircle,
  IconCircleCheck,
  IconCircleDot,
  IconClipboardList,
} from "@tabler/icons-react";

const useStyles = createStyles(() => ({
  pageWrapper: {
    minHeight: "calc(100vh - 200px)",
  },
  container: {
    backgroundColor: "var(--app-surface)",
    borderRadius: "12px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 220px)",
    overflowY: "auto",
    gap: "0.75rem",
    scrollbarWidth: "thin",
    scrollbarColor: "var(--app-border) transparent",
    "::-webkit-scrollbar": { width: "6px" },
    "::-webkit-scrollbar-track": { background: "transparent" },
    "::-webkit-scrollbar-thumb": { background: "var(--app-border)", borderRadius: "10px" },
  },
  taskContainer: {
    backgroundColor: "var(--app-surface)",
    borderRadius: "12px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 220px)",
    overflowY: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: "var(--app-border) transparent",
    "::-webkit-scrollbar": { width: "6px" },
    "::-webkit-scrollbar-track": { background: "transparent" },
    "::-webkit-scrollbar-thumb": { background: "var(--app-border)", borderRadius: "10px" },
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    marginBottom: "16px",
  },
  card: {
    borderRadius: "8px",
    padding: ".5rem",
    backgroundColor: "var(--app-bg)",
  },
  title: {
    borderRadius: "4px",
    padding: "0.3rem",
    borderLeftWidth: "5px",
    borderLeftStyle: "solid",
  },
  noteCardTitle: {
    backgroundColor: "var(--app-tint-amber)",
    borderLeftColor: "#CC7914",
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: ".1rem",
    backgroundColor: "var(--app-surface)",
    borderRadius: "10px",
    padding: "0.5rem",
    borderColor: "var(--app-border)",
    borderWidth: "2px",
    borderStyle: "solid",
    marginTop: "0.5rem",
  },

  // ─── Filter pills ───
  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "14px",
    flexWrap: "wrap",
  },
  filterPill: {
    fontFamily: "Inter",
    fontSize: 12.5,
    fontWeight: 600,
    padding: "6px 13px",
    borderRadius: "8px",
    cursor: "pointer",
    border: "1px solid transparent",
    transition: "all 0.15s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    userSelect: "none",
    whiteSpace: "nowrap",
    backgroundColor: "var(--app-surface-2)",
    color: "var(--app-text-muted)",
    "&:hover": { backgroundColor: "var(--app-border)" },
  },
  filterPillActive: {
    backgroundColor: "var(--app-text) !important",
    color: "#fff !important",
    "&:hover": { backgroundColor: "var(--app-text) !important" },
  },
  filterCount: {
    fontWeight: 700,
    opacity: 0.55,
  },
  filterPillAll: {
    backgroundColor: "var(--app-surface-2)",
    color: "var(--app-text-muted)",
    borderColor: "var(--app-border)",
    "&:hover": { backgroundColor: "var(--app-border)" },
  },
  filterPillAllActive: {
    backgroundColor: "var(--app-primary)",
    color: "var(--app-on-primary)",
    borderColor: "var(--app-primary)",
    "&:hover": { backgroundColor: "#FF8C00" },
  },
  filterPillInProgress: {
    backgroundColor: "#FFF8E1",
    color: "#92400E",
    borderColor: "#FDE68A",
    "&:hover": { backgroundColor: "#FEF3C7" },
  },
  filterPillInProgressActive: {
    backgroundColor: "#F59E0B",
    color: "#fff",
    borderColor: "#F59E0B",
    "&:hover": { backgroundColor: "#D97706" },
  },
  filterPillOverdue: {
    backgroundColor: "#FFF5F5",
    color: "#DC2626",
    borderColor: "#FECACA",
    "&:hover": { backgroundColor: "#FEE2E2" },
  },
  filterPillOverdueActive: {
    backgroundColor: "#EF4444",
    color: "#fff",
    borderColor: "#EF4444",
    "&:hover": { backgroundColor: "#DC2626" },
  },
  filterPillCompleted: {
    backgroundColor: "#F0FDF4",
    color: "#065F46",
    borderColor: "#BBF7D0",
    "&:hover": { backgroundColor: "#DCFCE7" },
  },
  filterPillCompletedActive: {
    backgroundColor: "#10B981",
    color: "#fff",
    borderColor: "#10B981",
    "&:hover": { backgroundColor: "#059669" },
  },

  // ─── Progress bar ───
  progressBarOuter: {
    height: 6,
    backgroundColor: "var(--app-surface-2)",
    borderRadius: 6,
    marginBottom: 14,
    overflow: "hidden",
    position: "relative",
  },
  progressBarInner: {
    height: "100%",
    borderRadius: 6,
    transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
  },
  "@keyframes shimmer": {
    "0%": { transform: "translateX(-100%)" },
    "100%": { transform: "translateX(100%)" },
  },
  progressShimmer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)",
    animation: "$shimmer 2s infinite ease-in-out",
  },

  // ─── Task card styles ───
  taskCard: {
    position: "relative",
    padding: "10px 14px 10px 18px",
    borderRadius: "10px",
    marginBottom: "6px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: "1px solid var(--app-border)",
    backgroundColor: "var(--app-surface)",
    overflow: "hidden",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      borderColor: "#D1D5DB",
      transform: "translateY(-1px)",
    },
  },
  taskCardOverdue: {
    borderColor: "#FCA5A5",
    backgroundColor: "#FFF5F5",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(239,68,68,0.1)",
      borderColor: "#F87171",
    },
  },
  taskCardInProgress: {
    borderColor: "#FDE68A",
    backgroundColor: "#FFFDF5",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(245,158,11,0.1)",
      borderColor: "#FBBF24",
    },
  },
  taskCardCompleted: {
    position: "relative",
    padding: "10px 14px 10px 18px",
    borderRadius: "10px",
    marginBottom: "6px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    border: "1px solid var(--app-border)",
    backgroundColor: "var(--app-surface-2)",
    overflow: "hidden",
    opacity: 0.65,
    "&:hover": {
      opacity: 0.85,
      backgroundColor: "var(--app-surface-2)",
    },
  },
  taskAccent: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    borderRadius: "10px 0 0 10px",
  },

  // ─── Status circle ───
  statusCircle: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    cursor: "pointer",
    transition: "all 0.25s ease",
    "&:hover": {
      transform: "scale(1.15)",
    },
  },
  statusCircleNotStarted: {
    border: "2px dashed #D1D5DB",
    backgroundColor: "transparent",
    "&:hover": {
      borderColor: "#9CA3AF",
      boxShadow: "0 0 8px rgba(156,163,175,0.4)",
    },
  },
  statusCircleInProgress: {
    border: "2px solid #F59E0B",
    backgroundColor: "#FEF3C7",
    "&:hover": {
      boxShadow: "0 0 10px rgba(245,158,11,0.45)",
    },
  },
  statusCircleComplete: {
    border: "2px solid #10B981",
    backgroundColor: "#D1FAE5",
    "&:hover": {
      boxShadow: "0 0 10px rgba(16,185,129,0.45)",
    },
  },

  // ─── Task type pill ───
  taskTypePill: {
    fontFamily: "Inter",
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: "10px",
    color: "#fff",
    whiteSpace: "nowrap",
    lineHeight: 1.6,
    letterSpacing: "0.02em",
  },
  taskTypePillAdmin: {
    backgroundColor: "var(--app-border)",
    color: "var(--app-text) !important",
  },
  taskTypePillSystem: {
    backgroundColor: "var(--app-primary)",
  },

  // ─── All-done / empty ───
  "@keyframes subtleBounce": {
    "0%, 100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-6px)" },
  },
  allDoneIcon: {
    animation: "$subtleBounce 2s ease-in-out infinite",
  },

  // ─── Task detail modal ───
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid #F5F5F5",
    "&:last-child": { borderBottom: "none" },
  },
  detailLabel: {
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 500,
    color: "var(--app-text-muted)",
  },
  detailValue: {
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 600,
    color: "var(--app-text)",
    textAlign: "right",
  },
  modalActionBtn: {
    fontFamily: "Inter",
    fontSize: 14,
    fontWeight: 700,
    padding: "12px 20px",
    borderRadius: "10px",
    cursor: "pointer",
    border: "none",
    transition: "all 0.2s ease",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    },
  },
  modalActionBtnInProgress: {
    backgroundColor: "#F59E0B",
    color: "#fff",
    "&:hover": { backgroundColor: "#D97706" },
  },
  modalActionBtnComplete: {
    backgroundColor: "#10B981",
    color: "#fff",
    "&:hover": { backgroundColor: "#059669" },
  },

  // ─── Modal status circle (larger) ───
  modalStatusCircle: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.25s ease",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
}));

const fmt12h = (time) => {
  if (!time) return "9:00 AM";
  if (time.includes("AM") || time.includes("PM")) return time;
  const [hh, mm] = time.split(":").map(Number);
  const p = hh >= 12 ? "PM" : "AM";
  const h = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
  return `${h}:${String(mm).padStart(2, "0")} ${p}`;
};

export const PendingActionsHeader = ({
  classes,
  loading,
  count,
  actions = [],
}) => {
  const hasPending = count > 0 || actions?.length > 0;

  return (
    <Box className={classes?.header}>
      <Text ff="Inter" fz={18} fw={600}>
        Pending Actions
      </Text>
      {loading ? (
        <Skeleton w={28} h={28} radius="xl" color="gray" />
      ) : (
        hasPending && (
          <Badge
            color="red"
            size="lg"
            radius="xl"
            variant="filled"
            p="xs"
            h={28}
            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            {count || 0}
          </Badge>
        )
      )}
    </Box>
  );
};

// ─── helpers for due-by display ───
const getDueByLabel = (task) => {
  if (task.scheduleType === "one_time" && task.dueDate) {
    return moment(task.dueDate).format("MMM DD, YYYY — h:mm A");
  }
  if (task.recurrence?.frequency === "daily") return `Today at ${fmt12h(task.recurrence.timeOfDay)}`;
  if (task.recurrence?.frequency === "weekly") {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return `${days[task.recurrence.dayOfWeek || 0]} at ${fmt12h(task.recurrence.timeOfDay)}`;
  }
  if (task.recurrence?.frequency === "monthly") return `Day ${task.recurrence.dayOfMonth || 1} at ${fmt12h(task.recurrence.timeOfDay)}`;
  return "—";
};

// ─── relative time helper ───
const getRelativeTimeLabel = (task) => {
  let dueMoment = null;

  if (task.scheduleType === "one_time" && task.dueDate) {
    dueMoment = moment(task.dueDate);
  } else if (task.recurrence?.frequency === "daily") {
    const timeStr = task.recurrence.timeOfDay || "09:00";
    dueMoment = moment(timeStr, "HH:mm");
    if (dueMoment.isBefore(moment())) {
      // if already past today, it was due earlier today
    }
  } else if (task.recurrence?.frequency === "weekly") {
    const timeStr = task.recurrence.timeOfDay || "09:00";
    dueMoment = moment().day(task.recurrence.dayOfWeek || 0).set({
      hour: parseInt(timeStr.split(":")[0], 10),
      minute: parseInt(timeStr.split(":")[1], 10),
    });
  } else if (task.recurrence?.frequency === "monthly") {
    const timeStr = task.recurrence.timeOfDay || "09:00";
    dueMoment = moment().date(task.recurrence.dayOfMonth || 1).set({
      hour: parseInt(timeStr.split(":")[0], 10),
      minute: parseInt(timeStr.split(":")[1], 10),
    });
  }

  if (!dueMoment || !dueMoment.isValid()) {
    return { text: getDueByLabel(task), isOverdue: !!task.isOverdue, urgency: task.isOverdue ? "overdue" : "none" };
  }

  const now = moment();
  const diff = dueMoment.diff(now);
  const isOverdue = task.isOverdue || diff < 0;

  if (isOverdue) {
    const absDiff = Math.abs(diff);
    const mins = Math.floor(absDiff / 60000);
    const hrs = Math.floor(mins / 60);
    const days = Math.floor(hrs / 24);

    let text;
    if (days > 0) text = `Overdue by ${days}d`;
    else if (hrs > 0) text = `Overdue by ${hrs}h`;
    else text = `Overdue by ${mins}m`;

    return { text, isOverdue: true, urgency: "overdue" };
  }

  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);

  if (hrs < 2) {
    let text;
    if (hrs > 0) text = `Due in ${hrs}h ${mins % 60}m`;
    else text = `Due in ${mins}m`;
    return { text, isOverdue: false, urgency: "soon" };
  }

  // Same day — show time
  if (dueMoment.isSame(now, "day")) {
    return { text: `Due at ${dueMoment.format("h:mm A")}`, isOverdue: false, urgency: "none" };
  }

  return { text: `Due ${dueMoment.fromNow()}`, isOverdue: false, urgency: "none" };
};

const getTaskTypeLabel = (task) => {
  if (task.taskType === "system") return "System Task";
  return "Admin Task";
};

const statusConfig = {
  complete: { label: "Complete", bg: "#ECFDF5", color: "#065F46", border: "#A7F3D0", accent: "#10B981" },
  in_progress: { label: "In Progress", bg: "#FFFBEB", color: "#92400E", border: "#FDE68A", accent: "#F59E0B" },
  not_started: { label: "Not Started", bg: "var(--app-surface-2)", color: "#6B7280", border: "var(--app-border)", accent: "#9CA3AF" },
};

// ─── Status Circle Component ───
const StatusCircle = ({ status, onClick, classes, size = 26, iconSize = 14 }) => {
  const circleClass =
    status === "complete"
      ? classes.statusCircleComplete
      : status === "in_progress"
      ? classes.statusCircleInProgress
      : classes.statusCircleNotStarted;

  return (
    <Box
      className={`${classes.statusCircle} ${circleClass}`}
      style={{ width: size, height: size, cursor: onClick ? "pointer" : "default" }}
      onClick={onClick}
    >
      {status === "complete" && <IconCheck size={iconSize} color="#10B981" stroke={3} />}
      {status === "in_progress" && <IconCircleDot size={iconSize} color="#F59E0B" stroke={2.5} />}
      {status === "not_started" && null}
    </Box>
  );
};

// ─── Task Detail Modal ───
const TaskDetailModal = ({ opened, onClose, task, onStatusUpdate, classes }) => {
  if (!task) return null;
  const sc = statusConfig[task.status] || statusConfig.not_started;
  const isOverdue = task.isOverdue;
  const relTime = getRelativeTimeLabel(task);
  const taskTypeIsSystem = task.taskType === "system";

  const handleCircleClick = (e) => {
    e.stopPropagation();
    // System tasks can only be completed by performing the linked action
    if (taskTypeIsSystem) return;
    if (task.status === "not_started" || task.status === "in_progress") {
      onStatusUpdate(task._id, "complete");
      onClose();
    }
  };

  const canStart = task.status === "not_started" && !taskTypeIsSystem;
  const canComplete = (task.status === "not_started" || task.status === "in_progress") && !taskTypeIsSystem;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      centered
      radius={16}
      size={420}
      padding={0}
      styles={{
        body: { padding: 0 },
        content: { overflow: "hidden" },
      }}
    >
      {/* Header */}
      <Box style={{ padding: "24px 24px 0", position: "relative" }}>
        {/* Close button */}
        <Box
          onClick={onClose}
          style={{
            position: "absolute", top: 16, right: 16,
            width: 28, height: 28, borderRadius: 6,
            backgroundColor: "var(--app-surface-2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", transition: "background 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--app-border)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--app-surface-2)")}
        >
          <Text fz={14} c="var(--app-text-muted)" lh={1} fw={500}>&#x2715;</Text>
        </Box>

        {/* Badges row */}
        <Flex align="center" gap={6} mb={12}>
          <Badge size="sm" radius="sm" variant="filled"
            styles={{ root: {
              backgroundColor: isOverdue ? "#DC2626" : task.status === "complete" ? "#10B981" : task.status === "in_progress" ? "#F59E0B" : "#171717",
              color: "#fff", fontFamily: "Inter", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em",
            } }}>
            {isOverdue && task.status !== "complete" ? "Overdue" : sc.label}
          </Badge>
          <Badge size="sm" radius="sm" variant="light"
            styles={{ root: {
              backgroundColor: taskTypeIsSystem ? "var(--app-tint-amber)" : "var(--app-surface-2)",
              color: taskTypeIsSystem ? "var(--app-primary)" : "var(--app-text-muted)",
              fontFamily: "Inter", fontWeight: 600, fontSize: 10, border: "none",
            } }}>
            {getTaskTypeLabel(task)}
          </Badge>
          {task.scheduleLabel && (
            <Badge size="sm" radius="sm" variant="light"
              styles={{ root: { backgroundColor: "var(--app-surface-2)", color: "var(--app-text-muted)", fontFamily: "Inter", fontWeight: 600, fontSize: 10, border: "none" } }}>
              {task.scheduleLabel}
            </Badge>
          )}
        </Flex>

        {/* Title */}
        <Text ff="Inter" fz={20} fw={700} c="var(--app-text)" lh={1.3}>
          {task.title}
        </Text>

        {task.status === "complete" && task.completedAt && (
          <Text ff="Inter" fz={12} c="var(--app-text-muted)" mt={4}>
            Completed {moment(task.completedAt).format("MMM DD, YYYY at h:mm A")}
          </Text>
        )}
      </Box>

      {/* Divider */}
      <Box style={{ height: 1, backgroundColor: "var(--app-surface-2)", margin: "16px 24px 0" }} />

      {/* Body */}
      <Box style={{ padding: "16px 24px 24px" }}>
        {/* Description */}
        {task.description && (
          <Text ff="Inter" fz={13} c="var(--app-text-muted)" lh={1.6} mb={16}>
            {task.description}
          </Text>
        )}

        {/* Info rows */}
        <Box style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          <Flex align="center" justify="space-between" style={{ padding: "10px 14px", backgroundColor: "var(--app-surface-2)", borderRadius: 8 }}>
            <Text ff="Inter" fz={12} fw={500} c="var(--app-text-muted)">Due</Text>
            <Flex align="center" gap={6}>
              <IconCalendarDue size={14} color={relTime.isOverdue ? "#DC2626" : "var(--app-text)"} stroke={2} />
              <Text ff="Inter" fz={13} fw={600} c={relTime.isOverdue ? "#DC2626" : "var(--app-text)"}>
                {relTime.text}
              </Text>
            </Flex>
          </Flex>
          <Flex align="center" justify="space-between" style={{ padding: "10px 14px", backgroundColor: "var(--app-surface-2)", borderRadius: 8 }}>
            <Text ff="Inter" fz={12} fw={500} c="var(--app-text-muted)">Schedule</Text>
            <Text ff="Inter" fz={13} fw={600} c="var(--app-text)">
              {task.scheduleLabel || (task.scheduleType === "one_time" ? "One-time" : "Recurring")}
            </Text>
          </Flex>
          <Flex align="center" justify="space-between" style={{ padding: "10px 14px", backgroundColor: "var(--app-surface-2)", borderRadius: 8 }}>
            <Text ff="Inter" fz={12} fw={500} c="var(--app-text-muted)">Task Type</Text>
            <Flex align="center" gap={6}>
              <Box style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: taskTypeIsSystem ? "var(--app-primary)" : "#D1D5DB" }} />
              <Text ff="Inter" fz={13} fw={600} c="var(--app-text)">{getTaskTypeLabel(task)}</Text>
            </Flex>
          </Flex>
          {(task.linkedNoteType || task.linkedTaskType) && (
            <Flex align="center" justify="space-between" style={{ padding: "10px 14px", backgroundColor: "var(--app-surface-2)", borderRadius: 8 }}>
              <Text ff="Inter" fz={12} fw={500} c="var(--app-text-muted)">{task.linkedNoteType ? "Note Type" : "Task Type Required"}</Text>
              <Text ff="Inter" fz={13} fw={600} c="var(--app-text)">
                {task.linkedNoteType?.name || task.linkedTaskType?.name || "—"}
              </Text>
            </Flex>
          )}
        </Box>

        {/* Action buttons */}
        {task.status !== "complete" && onStatusUpdate && (canStart || canComplete) && (
          <Flex gap={10}>
            {canStart && (
              <Button
                fullWidth variant="outline" radius={8} size="sm"
                styles={{ root: { fontFamily: "Inter", fontWeight: 600, fontSize: 13, color: "var(--app-text)", borderColor: "var(--app-border)", "&:hover": { backgroundColor: "var(--app-surface-2)", borderColor: "#D1D5DB" } } }}
                leftIcon={<IconPlayerPlay size={15} />}
                onClick={() => { onStatusUpdate(task._id, "in_progress"); }}
              >
                Start Task
              </Button>
            )}
            {canComplete && (
              <Button
                fullWidth radius={8} size="sm"
                styles={{ root: { fontFamily: "Inter", fontWeight: 600, fontSize: 13, backgroundColor: "#171717", color: "#fff", "&:hover": { backgroundColor: "var(--app-text)" } } }}
                leftIcon={<IconCheck size={15} />}
                onClick={() => { onStatusUpdate(task._id, "complete"); onClose(); }}
              >
                Mark Complete
              </Button>
            )}
          </Flex>
        )}

        {/* Completed state */}
        {task.status === "complete" && (
          <Flex align="center" justify="center" gap={6} style={{ padding: "12px 0 0" }}>
            <IconCheck size={16} color="#10B981" stroke={3} />
            <Text ff="Inter" fz={13} fw={600} c="#10B981">Task completed</Text>
          </Flex>
        )}
      </Box>
    </Modal>
  );
};

const PendingAction = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [complianceTasks, setComplianceTasks] = useState([]);
  const [complianceTasksLoading, setComplianceTasksLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailOpened, { open: openTaskDetail, close: closeTaskDetail }] = useDisclosure(false);
  // Always "all": active tasks list first, completed tasks in a section below.
  const [activeFilter] = useState("all");

  const { singleNote } = useSelector((state) => state?.note);
  const { profile } = useSelector((state) => state?.profile);
  const [
    pendingActionDrawerOpened,
    { open: pendingActionDrawerOpen, close: pendingActionDrawerClose },
  ] = useDisclosure(false);

  const {
    dashboardPendingActions,
    dashboardPendingActionsLoading,
    dashboardPendingActionsCount,
  } = useSelector((state) => state?.dashboard);

  const { staff } = useSelector((state) => state.staff);

  const fetchComplianceTasks = async () => {
    setComplianceTasksLoading(true);
    const result = await getMyComplianceTasks(1, 50);
    if (result) {
      setComplianceTasks(result.tasks || []);
    }
    setComplianceTasksLoading(false);
  };

  useEffect(() => {
    fetchComplianceTasks();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const noteId = params.get("noteId");
    if (noteId) {
      getSingleNote(noteId);
      setIsOpen(true);
    }
  }, []);

  useEffect(() => {
    if (singleNote) {
      setSelectedNote(singleNote);
    }
  }, [singleNote]);

  const { classes } = useStyles();

  const hasPermissions = profile?.user?.permissions;

  const handleStatusUpdate = async (taskId, newStatus) => {
    const result = await updateOperationalTaskStatus(taskId, newStatus);
    if (result) fetchComplianceTasks();
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    openTaskDetail();
  };

  // ─── Filtering logic ───
  const filteredTasks = complianceTasks.filter((t) => {
    if (activeFilter === "all") return true;
    if (activeFilter === "in_progress") return t.status === "in_progress";
    if (activeFilter === "overdue") return t.isOverdue && t.status !== "complete";
    if (activeFilter === "completed") return t.status === "complete";
    return true;
  });

  const activeTasks = filteredTasks.filter((t) => t.status !== "complete");
  const completedTasks = filteredTasks.filter((t) => t.status === "complete");

  // Progress percentage (from full list, not filtered)
  const totalTasks = complianceTasks.length;
  const totalCompleted = complianceTasks.filter((t) => t.status === "complete").length;
  const progressPct = totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0;

  // Progress bar gradient: purple → green as percentage increases
  const getProgressGradient = (pct) => {
    if (pct === 0) return "var(--app-primary)";
    if (pct >= 100) return "#10B981";
    return `linear-gradient(90deg, #FFAF4A, #10B981)`;
  };

  const handleStatusCircleClick = (e, task) => {
    e.stopPropagation();
    // System tasks can only be completed by performing the linked action (e.g., creating a note/task)
    if (task.taskType === "system") return;
    if (task.status === "not_started" || task.status === "in_progress") {
      handleStatusUpdate(task._id, "complete");
    }
    // complete → no action
  };

  // ─── Left column: My Tasks ───
  // `bare` renders the inner content only (no card chrome / no big title) for
  // use inside the combined staff Tasks/Calendar tabbed card.
  const renderTasksColumn = (bare = false) => (
    <Box className={bare ? undefined : classes.taskContainer}>
      {!bare && (
        <Flex align="center" justify="space-between" mb={12}>
          <Flex align="center" gap={10}>
            <Text ff="Inter" fz={18} fw={700} c="var(--app-text)">
              My Tasks
            </Text>
            {activeTasks.length > 0 && (
              <Box style={{
                backgroundColor: "var(--app-primary)",
                borderRadius: 20,
                height: 24,
                minWidth: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0 8px",
              }}>
                <Text ff="Inter" fz={12} fw={700} c="var(--app-on-primary)">{complianceTasks.filter(t => t.status !== "complete").length}</Text>
              </Box>
            )}
          </Flex>
          {totalTasks > 0 && (
            <Text ff="Inter" fz={12} c="var(--app-text-muted)" fw={500}>
              {totalCompleted}/{totalTasks} done
            </Text>
          )}
        </Flex>
      )}

      {/* Filter pills removed — active tasks show first, completed appear in a
          section at the bottom of the list. */}

      {complianceTasksLoading ? (
        <Flex justify="center" py={40}>
          <Loader color="var(--app-primary)" size="sm" />
        </Flex>
      ) : complianceTasks.length === 0 ? (
        /* ─── No tasks empty state ─── */
        <Flex direction="column" align="center" justify="center" style={{ flex: 1, gap: 10 }}>
          <IconClipboardList size={48} color="#D1D5DB" stroke={1.3} />
          <Text ff="Inter" fz={15} fw={600} c="var(--app-text-muted)" align="center">
            No tasks assigned yet
          </Text>
          <Text ff="Inter" fz={12} c="#D1D5DB" align="center">
            Tasks will appear here once they are created for you.
          </Text>
        </Flex>
      ) : filteredTasks.length === 0 ? (
        /* ─── Filter returned nothing ─── */
        <Flex direction="column" align="center" justify="center" style={{ flex: 1, gap: 8 }}>
          <IconFilter size={36} color="#D1D5DB" stroke={1.5} />
          <Text ff="Inter" fz={14} c="var(--app-text-muted)" align="center">
            No tasks match this filter.
          </Text>
        </Flex>
      ) : (
        <Box>
          {/* All done message */}
          {activeTasks.length === 0 && completedTasks.length > 0 && activeFilter !== "completed" && (
            <Box style={{
              textAlign: "center",
              padding: "24px 16px",
              backgroundColor: "#F0FDF4",
              borderRadius: 12,
              border: "1px solid #BBF7D0",
              marginBottom: 12,
            }}>
              <Box className={classes.allDoneIcon} style={{ display: "inline-block", marginBottom: 6 }}>
                <Text fz={32} lh={1}>&#127881;</Text>
              </Box>
              <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 4 }}>
                <IconCircleCheck size={22} color="#10B981" stroke={2.5} />
                <Text ff="Inter" fz={16} c="#065F46" fw={700}>All tasks completed!</Text>
              </Box>
              <Text ff="Inter" fz={12} c="#6EE7B7" fw={500}>Great work today</Text>
            </Box>
          )}

          {/* Active task cards */}
          {activeTasks.map((task) => {
            const isOverdue = task.isOverdue;
            const isInProgress = task.status === "in_progress";
            const taskTypeIsSystem = task.taskType === "system";
            const typeColor = taskTypeIsSystem ? "var(--app-primary)" : "var(--app-text-muted)";
            const accentColor = isOverdue ? "#EF4444" : isInProgress ? "#F59E0B" : typeColor;
            const relTime = getRelativeTimeLabel(task);

            return (
              <Box
                key={task._id}
                className={`${classes.taskCard} ${isOverdue ? classes.taskCardOverdue : isInProgress ? classes.taskCardInProgress : ""}`}
                onClick={() => handleTaskClick(task)}
              >
                {/* Card content */}
                <Box pl={6}>
                  {/* Line 1: Checkbox circle + Task title + Task type pill */}
                  <Flex align="center" gap={10} mb={6}>
                    <StatusCircle
                      status={task.status}
                      classes={classes}
                      onClick={taskTypeIsSystem ? undefined : (e) => handleStatusCircleClick(e, task)}
                    />
                    <Text ff="Inter" fz={14} fw={600} c="var(--app-text)" lh={1.3} style={{ flex: 1, minWidth: 0 }} lineClamp={1}>
                      {task.title}
                    </Text>
                    <Flex align="center" gap={4}>
                      {taskTypeIsSystem && task.systemCategory && (
                        <Box className={classes.taskTypePill} style={{ backgroundColor: task.systemCategory === "note" ? "var(--app-tint-blue)" : "#F0FDF4", color: task.systemCategory === "note" ? "#2563EB" : "#16A34A" }}>
                          {task.systemCategory === "note" ? "Note" : "Task"}
                        </Box>
                      )}
                      <Box className={`${classes.taskTypePill} ${taskTypeIsSystem ? classes.taskTypePillSystem : classes.taskTypePillAdmin}`}>
                        {taskTypeIsSystem ? "System" : "Admin"}
                      </Box>
                    </Flex>
                  </Flex>

                  {/* Line 2: Status badge + schedule badge + relative due time + chevron */}
                  <Flex align="center" gap={8} pl={36}>
                    <Badge
                      size="sm"
                      radius="md"
                      variant="dot"
                      color={isOverdue ? "red" : isInProgress ? "yellow" : "gray"}
                      styles={{
                        root: { textTransform: "none", paddingLeft: 10, fontFamily: "Inter", fontWeight: 600 },
                      }}
                    >
                      {isOverdue ? "Overdue" : isInProgress ? "In Progress" : "Not Started"}
                    </Badge>
                    {task.scheduleLabel && (
                      <Badge size="sm" radius="md" variant="light" color="gray"
                        styles={{ root: { textTransform: "none", fontFamily: "Inter", fontWeight: 500, fontSize: 10, backgroundColor: "var(--app-surface-2)", color: "var(--app-text-muted)", border: "none" } }}>
                        {task.scheduleLabel}
                      </Badge>
                    )}
                    <Flex align="center" gap={4} style={{ flex: 1 }}>
                      <IconCalendarDue size={13} color={relTime.isOverdue ? "#EF4444" : relTime.urgency === "soon" ? "#D97706" : "var(--app-text-muted)"} />
                      <Text ff="Inter" fz={11} c={relTime.isOverdue ? "#EF4444" : relTime.urgency === "soon" ? "#D97706" : "var(--app-text-muted)"} fw={500}>
                        {relTime.text}
                      </Text>
                    </Flex>
                    <IconChevronRight size={16} color="#D1D5DB" style={{ flexShrink: 0 }} />
                  </Flex>
                </Box>
              </Box>
            );
          })}

          {/* Completed tasks */}
          {completedTasks.length > 0 && (
            <Box mt={16}>
              {activeFilter !== "completed" && (
                <Flex align="center" gap={8} mb={8}>
                  <Box style={{ flex: 1, height: 1, backgroundColor: "var(--app-border)" }} />
                  <Text ff="Inter" fz={11} c="var(--app-text-muted)" fw={600} style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Completed ({completedTasks.length})
                  </Text>
                  <Box style={{ flex: 1, height: 1, backgroundColor: "var(--app-border)" }} />
                </Flex>
              )}
              {completedTasks.map((task) => {
                const taskTypeIsSystem = task.taskType === "system";
                return (
                  <Box
                    key={task._id}
                    className={classes.taskCardCompleted}
                    onClick={() => handleTaskClick(task)}
                  >

                    <Box pl={6}>
                      {/* Line 1: completed circle + title + task type */}
                      <Flex align="center" gap={10} mb={4}>
                        <StatusCircle
                          status="complete"
                          classes={classes}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Text ff="Inter" fz={13} fw={500} c="var(--app-text-muted)" td="line-through" lh={1.3} style={{ flex: 1, minWidth: 0 }} lineClamp={1}>
                          {task.title}
                        </Text>
                        <Flex align="center" gap={4}>
                          {taskTypeIsSystem && task.systemCategory && (
                            <Box className={classes.taskTypePill} style={{ backgroundColor: task.systemCategory === "note" ? "var(--app-tint-blue)" : "#F0FDF4", color: task.systemCategory === "note" ? "#2563EB" : "#16A34A", opacity: 0.5 }}>
                              {task.systemCategory === "note" ? "Note" : "Task"}
                            </Box>
                          )}
                          <Box className={`${classes.taskTypePill} ${taskTypeIsSystem ? classes.taskTypePillSystem : classes.taskTypePillAdmin}`} style={{ opacity: 0.5 }}>
                            {taskTypeIsSystem ? "System" : "Admin"}
                          </Box>
                        </Flex>
                      </Flex>
                      {/* Line 2: schedule + completed time */}
                      <Flex align="center" gap={6} pl={36}>
                        {task.scheduleLabel && (
                          <Badge size="xs" radius="md" variant="light" color="gray"
                            styles={{ root: { textTransform: "none", fontFamily: "Inter", fontWeight: 500, fontSize: 10, backgroundColor: "var(--app-surface-2)", color: "#D1D5DB", border: "none" } }}>
                            {task.scheduleLabel}
                          </Badge>
                        )}
                        {task.completedAt && (
                          <Text ff="Inter" fz={11} c="#D1D5DB">
                            Completed {moment(task.completedAt).format("h:mm A")}
                          </Text>
                        )}
                        <Box style={{ flex: 1 }} />
                        <IconChevronRight size={14} color="var(--app-border)" style={{ flexShrink: 0 }} />
                      </Flex>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  // ─── Right column: Pending Actions ───
  const renderPendingColumn = () => (
    <Box className={classes.container}>
      <PendingActionsHeader
        classes={classes}
        loading={dashboardPendingActionsLoading}
        count={dashboardPendingActionsCount}
        actions={dashboardPendingActions}
      />
      {dashboardPendingActionsLoading ? (
        Array.from({ length: 3 }).map((_, index) => (
          <Skeleton
            key={index}
            mb="md"
            radius="md"
            height={200}
            color="gray"
            c={"var(--app-border)"}
            visible={true}
          />
        ))
      ) : (Array.isArray(dashboardPendingActions) ? dashboardPendingActions : dashboardPendingActions?.data || []).length > 0 ? (
        <>
          {(Array.isArray(dashboardPendingActions) ? dashboardPendingActions : dashboardPendingActions?.data || [])
            .filter((item) => {
              if (item.type === "document") return true;
              // Only hide notes if permissions.note is explicitly false
              if (hasPermissions && profile?.user?.permissions?.note === false) return false;
              return true;
            })
            ?.map((item, index) => {
              if (item?.type === "document") {
                return <DocumentNotificationCard key={item?.id} item={item} />;
              } else if (item.type === "note") {
                return (
                  <Box className={classes.card} key={index}>
                    <Box className={`${classes.title} ${classes.noteCardTitle}`}>
                      <Text ff={"Inter"} fz={12} fw={700}>
                        Note
                      </Text>
                    </Box>
                    <Box className={classes.body}>
                      <Badge
                        size="md"
                        radius="xl"
                        color="gray"
                        c={"#525252"}
                        tt={"uppercase"}
                        w={"fit-content"}
                      >
                        {item?.fields?.noteType || item?.noteId?.name}
                      </Badge>
                      <Text ff={"Inter"} fz={24} fw={700}>
                        {
                          staff?.staffs?.find(
                            (staffMember) => item?.createdBy === staffMember?.id
                          )?.name
                        }
                      </Text>
                      <Text ff={"Inter"} fz={14} c={"var(--app-text-muted)"} fw={500}>
                        {moment(item?.noteDate || item?.createdAt).format(
                          "MMM DD, hh:mmA"
                        )}
                      </Text>
                      <Text ff={"Inter"} fz={14} c={"var(--app-text-muted)"} fw={400}>
                        {item?.fields?.Description
                          ? item.fields.Description.length > 100
                            ? `${item.fields.Description.slice(0, 100)}...`
                            : item.fields.Description
                          : "No Description Found"}
                      </Text>
                      <Center>
                        <Button
                          fz={12}
                          fw={500}
                          fullWidth
                          c={"var(--app-text)"}
                          ff={"Inter"}
                          variant="white"
                          onClick={() => {
                            setSelectedNote(item);
                            setIsOpen(true);
                          }}
                        >
                          View Details
                        </Button>
                      </Center>
                    </Box>
                  </Box>
                );
              }
              return null;
            })}
          <Button
            size="md"
            fullWidth
            c={"var(--app-text)"}
            variant="white"
            onClick={pendingActionDrawerOpen}
          >
            View All
          </Button>
        </>
      ) : (
        <Text
          ff={"Inter"}
          fz={14}
          c={"var(--app-text-muted)"}
          fw={500}
          align="center"
          my={"auto"}
        >
          No pending actions found.
        </Text>
      )}
    </Box>
  );

  // ─── Monthly Calendar (for staff users) ───
  const userRole = profile?.user?.role;
  const [calMonth, setCalMonth] = useState(() => moment());
  // Combined Tasks/Calendar card tab (My Tasks is the default tab).
  const [dashTab, setDashTab] = useState("tasks");
  const [selectedDay, setSelectedDay] = useState(() => moment());
  // Staff's scheduled shifts + appointments for the visible month (Scheduler data).
  const [monthShifts, setMonthShifts] = useState([]);
  const navigate = useNavigate();
  // Read-only detail side panel for a clicked shift/appointment.
  const [detailShift, setDetailShift] = useState(null);
  const openShiftDetail = async (s) => {
    // Virtual open shifts (generated from recurring schedules) have no DB doc to fetch.
    if (s?.virtual) {
      setDetailShift(s);
      return;
    }
    const id = s?._id || s?.id;
    const full = id ? await schedulerService.getShift(id) : null;
    setDetailShift(full || s);
  };

  useEffect(() => {
    if (!isStaff(userRole)) return;
    let cancelled = false;
    const from = isoDate(calMonth.clone().startOf("month").toDate());
    const to = isoDate(calMonth.clone().endOf("month").toDate());
    schedulerService
      .getShifts({ from, to, includeAppointments: true })
      .then((data) => {
        if (cancelled) return;
        setMonthShifts((data || []).filter((s) => s.status !== "open"));
      })
      .catch(() => { if (!cancelled) setMonthShifts([]); });
    return () => { cancelled = true; };
  }, [calMonth, userRole]);

  // Map of YYYY-MM-DD → that day's shifts, sorted by start time.
  const shiftsByDay = useMemo(() => {
    const m = {};
    (monthShifts || []).forEach((s) => {
      const key = moment(s.date).format("YYYY-MM-DD");
      (m[key] = m[key] || []).push(s);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.startMin || 0) - (b.startMin || 0)));
    return m;
  }, [monthShifts]);

  const selectedDayShifts = shiftsByDay[selectedDay.format("YYYY-MM-DD")] || [];

  const calendarData = useMemo(() => {
    const start = calMonth.clone().startOf("month");
    const end = calMonth.clone().endOf("month");
    const startDay = start.day(); // 0=Sun
    const daysInMonth = calMonth.daysInMonth();
    const today = moment();
    const weeks = [];
    let day = 1 - startDay;
    while (day <= daysInMonth) {
      const week = [];
      for (let i = 0; i < 7; i++, day++) {
        if (day < 1 || day > daysInMonth) {
          week.push(null);
        } else {
          const date = start.clone().date(day);
          week.push({
            day,
            mDate: date,
            dateKey: date.format("YYYY-MM-DD"),
            isToday: date.isSame(today, "day"),
            isCurrentMonth: true,
          });
        }
      }
      weeks.push(week);
    }
    return { weeks, monthLabel: calMonth.format("MMMM YYYY") };
  }, [calMonth]);

  const renderCalendarColumn = (bare = false) => (
    <Box style={bare ? undefined : {
      backgroundColor: "var(--app-surface)",
      borderRadius: 16,
      border: "1px solid #F0F0F0",
      padding: "24px 20px",
    }}>
      {/* Header */}
      <Flex align="center" mb={20} style={{ position: "relative" }}>
        <Flex align="center" gap={4} style={{ margin: "0 auto" }}>
          <Box
            onClick={() => setCalMonth((m) => m.clone().subtract(1, "month"))}
            style={{ cursor: "pointer", width: 28, height: 28, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "var(--app-surface-2)", transition: "background 0.15s" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--app-border)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--app-surface-2)")}
          >
            <IconChevronLeft size={16} color="var(--app-text-muted)" />
          </Box>
          <Text ff="Inter" fz={13} fw={600} c="var(--app-text)" style={{ minWidth: 120, textAlign: "center" }}>
            {calendarData.monthLabel}
          </Text>
          <Box
            onClick={() => setCalMonth((m) => m.clone().add(1, "month"))}
            style={{ cursor: "pointer", width: 28, height: 28, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "var(--app-surface-2)", transition: "background 0.15s" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--app-border)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--app-surface-2)")}
          >
            <IconChevronRight size={16} color="var(--app-text-muted)" />
          </Box>
        </Flex>
        <Box
          onClick={() => navigate("/home/scheduler", { state: { tab: "schedule" } })}
          style={{ cursor: "pointer", position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 8, backgroundColor: "var(--app-surface-2)", border: "1px solid var(--app-border)", transition: "background 0.15s" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--app-border)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--app-surface-2)")}
        >
          <Text ff="Inter" fz={12.5} fw={600} c="var(--app-text)">My Schedule</Text>
        </Box>
      </Flex>

      {/* Today highlight */}
      <Box style={{
        background: "linear-gradient(135deg, #000 0%, #1a1a1a 100%)",
        borderRadius: 12,
        padding: "16px 18px",
        marginBottom: 20,
      }}>
        <Text ff="Inter" fz={12} fw={600} c="rgba(255,255,255,0.5)" style={{ textTransform: "uppercase", letterSpacing: "0.06em" }} mb={4}>
          Today
        </Text>
        <Text ff="Inter" fz={22} fw={800} c="#fff" lh={1.2}>
          {moment().format("dddd")}
        </Text>
        <Text ff="Inter" fz={14} fw={500} c="var(--app-primary)" mt={2}>
          {moment().format("MMMM D, YYYY")}
        </Text>
      </Box>

      {/* Day headers */}
      <Box style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0, marginBottom: 4 }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <Text key={d} ff="Inter" fz={11} fw={600} c="var(--app-text-muted)" ta="center" py={6} style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {d}
          </Text>
        ))}
      </Box>

      {/* Calendar grid */}
      {calendarData.weeks.map((week, wi) => (
        <Box key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0 }}>
          {week.map((cell, di) => {
            const hasShifts = cell && (shiftsByDay[cell.dateKey] || []).length > 0;
            const isSelected = cell && selectedDay.format("YYYY-MM-DD") === cell.dateKey;
            return (
              <Box key={di} style={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                height: 40,
              }}>
                {cell && (
                  <Box
                    onClick={() => setSelectedDay(cell.mDate.clone())}
                    style={{
                      width: 34, height: 34, borderRadius: "50%",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer",
                      backgroundColor: cell.isToday ? "#000" : isSelected ? "#FFF3CD" : "transparent",
                      border: isSelected && !cell.isToday ? "1px solid var(--app-primary)" : "1px solid transparent",
                      transition: "background 0.15s",
                    }}
                  >
                    <Text
                      ff="Inter"
                      fz={13}
                      fw={cell.isToday || isSelected ? 700 : 400}
                      c={cell.isToday ? "var(--app-primary)" : "var(--app-text)"}
                    >
                      {cell.day}
                    </Text>
                  </Box>
                )}
                <Box style={{
                  width: 5, height: 5, borderRadius: "50%", marginTop: 2,
                  backgroundColor: hasShifts ? "var(--app-primary)" : "transparent",
                }} />
              </Box>
            );
          })}
        </Box>
      ))}

      {/* Selected-day agenda — schedule + appointments from Time Management */}
      <Box mt={16} pt={16} style={{ borderTop: "1px solid #F0F0F0" }}>
        <Text ff="Inter" fz={12} fw={700} c="var(--app-text-muted)" mb={10} style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {selectedDay.isSame(moment(), "day") ? "Today" : selectedDay.format("dddd, MMM D")}
        </Text>
        {selectedDayShifts.length === 0 ? (
          <Text ff="Inter" fz={13} c="var(--app-text-muted)">No shifts or appointments scheduled.</Text>
        ) : (
          <Flex direction="column" gap={8}>
            {selectedDayShifts.map((s) => {
              const meta = entryMeta(s);
              const isAppt = s.kind === "appointment";
              const accent = isAppt ? "#6d28d9" : s.type === "facility" ? "#0e7490" : "#b45309";
              const title = isAppt
                ? (s.apptTitle || "Appointment")
                : (meta.client || meta.facility || "Shift");
              const sub = [
                s.allDay ? "All day" : fmtRange(s.startMin, s.endMin),
                isAppt ? (meta.facility || meta.client || null) : (meta.facility && meta.client ? meta.facility : null),
              ].filter(Boolean).join(" · ");
              return (
                <Flex key={s._id || s.id} align="center" gap={10} onClick={() => openShiftDetail(s)} style={{ border: "1px solid #F0F0F0", borderRadius: 10, padding: "9px 12px", cursor: "pointer", transition: "background 0.15s, border-color 0.15s" }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--app-surface-2)"; e.currentTarget.style.borderColor = "var(--app-border)"; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.borderColor = "#F0F0F0"; }}>
                  <Box style={{ width: 4, alignSelf: "stretch", borderRadius: 4, backgroundColor: accent }} />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text ff="Inter" fz={13} fw={700} c="var(--app-text)" truncate>{title}</Text>
                    <Text ff="Inter" fz={11.5} c="var(--app-text-muted)" truncate>{sub}</Text>
                  </Box>
                  <Text ff="Inter" fz={10.5} fw={700} c={accent} style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {isAppt ? "Appt" : s.type === "facility" ? "Facility" : "Client"}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        )}
      </Box>

      {/* Back to today button (if navigated away) */}
      {!calMonth.isSame(moment(), "month") && (
        <Flex justify="center" mt={12}>
          <Box
            onClick={() => setCalMonth(moment())}
            style={{
              cursor: "pointer", padding: "6px 16px", borderRadius: 8,
              backgroundColor: "var(--app-surface-2)", border: "1px solid var(--app-border)",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--app-surface-2)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--app-surface-2)")}
          >
            <Text ff="Inter" fz={12} fw={600} c="var(--app-text-muted)">Back to Today</Text>
          </Box>
        </Flex>
      )}
    </Box>
  );

  // Combined staff card: My Tasks (default) + Calendar as tabs.
  const renderStaffPanel = () => (
    <Box className={classes.taskContainer}>
      <Flex align="center" justify="space-between" mb={18}>
        {/* Segmented tab control */}
        <Box style={{ display: "inline-flex", gap: 4, backgroundColor: "var(--app-surface-2)", borderRadius: 12, padding: 4 }}>
          {[["tasks", "My Tasks"], ["calendar", "Calendar"]].map(([key, label]) => (
            <Box
              key={key}
              onClick={() => setDashTab(key)}
              style={{
                cursor: "pointer", padding: "7px 18px", borderRadius: 9,
                fontFamily: "Inter", fontSize: 14, fontWeight: 700,
                backgroundColor: dashTab === key ? "var(--app-surface)" : "transparent",
                color: dashTab === key ? "var(--app-text)" : "var(--app-text-muted)",
                boxShadow: dashTab === key ? "0 1px 2px rgba(16,24,40,0.10)" : "none",
                transition: "all 0.15s",
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
        {dashTab === "tasks" && totalTasks > 0 && (
          <Text ff="Inter" fz={12.5} c="var(--app-text-muted)" fw={600}>{totalCompleted} of {totalTasks} done</Text>
        )}
      </Flex>
      {dashTab === "tasks" ? renderTasksColumn(true) : renderCalendarColumn(true)}
    </Box>
  );

  return (
    <Box className={classes.pageWrapper}>
      {isStaff(userRole) ? (
        renderStaffPanel()
      ) : (
        <Grid gutter={16}>
          {/* Left: Tasks — 2/3 width */}
          <Grid.Col span={8}>
            {renderTasksColumn()}
          </Grid.Col>
          {/* Right: Pending Actions (admin) — 1/3 width */}
          <Grid.Col span={4}>
            {renderPendingColumn()}
          </Grid.Col>
        </Grid>
      )}

      {/* Task Detail Modal */}
      <TaskDetailModal
        opened={taskDetailOpened}
        onClose={closeTaskDetail}
        task={selectedTask}
        onStatusUpdate={handleStatusUpdate}
        classes={classes}
      />

      {/* Note Modal */}
      {selectedNote && (
        <NoteModalNew
          view={true}
          opened={isOpen}
          data={selectedNote}
          title={"View Note"}
          close={() => setIsOpen(false)}
        />
      )}
      <PendingActionsDrawer
        opened={pendingActionDrawerOpened}
        close={pendingActionDrawerClose}
        setSelectedNote={setSelectedNote}
        setIsOpen={setIsOpen}
      />
      <ShiftDrawer opened={!!detailShift} shift={detailShift} onClose={() => setDetailShift(null)} readOnly />
    </Box>
  );
};

export default PendingAction;
